import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppWindow, FileText, FolderGit2, Laptop, Layers, Plus, X } from 'lucide-react';
import { getServices, getServiceImpact, linkServiceAsset, unlinkServiceAsset, linkServicePool, unlinkServicePool, setServiceTier, createServiceEnvironment, updateServiceEnvironment, deleteServiceEnvironment } from '@/api/services';
import { listAssets } from '@/api/assets';
import { listSoftware } from '@/api/software';
import { useAuth } from '@/auth/useAuth';
import { ApiError } from '@/api/client';
import { EntityPicker, type PickerOption } from '@/components/EntityPicker';
import { formatDate, formatMoney } from '@/lib/dateFormat';
import { SERVICE_TIERS, tierMeta } from '@/lib/serviceTier';
import type { Service, ServiceImpact, ServiceEnvironment, ServiceEnvironmentWrite } from '@/types/oscar';

/**
 * Services / impact view (CMDB Slice 3). The shared service catalogue with, for
 * each service, what it touches across tenants — supporting assets (Oscar),
 * CAB changes, and projects. Answers "if we change/decommission this, what's
 * affected?". Helpdesk/Admin can link supporting assets here (the reverse of the
 * asset-detail Services tab); sourced from the core.vService* views.
 */
const hasNoOwner = (s: Service) => !s.PrimaryOwnerEmail;
const hasNoAssets = (s: Service) => s.AssetCount === 0;
const hasNoTier = (s: Service) => s.Tier == null;

// Asset search for the link picker — serial / hostname / tag, server-side.
const assetSearch = (q: string, signal?: AbortSignal): Promise<PickerOption[]> =>
  listAssets({ search: q, pageSize: 20 }, signal).then((r) => r.Items.map((a) => ({
    id: a.AssetId,
    label: a.SerialNumber || a.Hostname || a.AssetTag || `Asset #${a.AssetId}`,
    sublabel: [a.Make, a.Model].filter(Boolean).join(' ') || a.Category,
  })));

// Licence-pool search for the software link picker — the small pool list, filtered client-side.
const poolSearch = (q: string, signal?: AbortSignal): Promise<PickerOption[]> =>
  listSoftware(signal).then((list) =>
    list
      .filter((p) => p.Title.toLowerCase().includes(q.toLowerCase()))
      .slice(0, 20)
      .map((p) => ({ id: p.SoftwarePoolId, label: p.Title, sublabel: p.LicenceType })));

export function ServicesPage() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [onlyNoOwner, setOnlyNoOwner] = useState(false);
  const [onlyNoAssets, setOnlyNoAssets] = useState(false);
  const [onlyUntiered, setOnlyUntiered] = useState(false);
  // Active/Deactivated filter — defaults to Active so deactivated services are hidden.
  const [status, setStatus] = useState<'active' | 'inactive' | 'all'>('active');

  const list = useQuery({ queryKey: ['services'], queryFn: ({ signal }) => getServices(signal) });
  const impact = useQuery({
    queryKey: ['service-impact', selectedId],
    queryFn: ({ signal }) => getServiceImpact(selectedId!, signal),
    enabled: selectedId != null,
  });

  const services = list.data ?? [];
  const noOwnerCount = services.filter(hasNoOwner).length;
  const noAssetsCount = services.filter(hasNoAssets).length;
  const untieredCount = services.filter(hasNoTier).length;
  const q = search.trim().toLowerCase();
  const visible = services.filter((s) => {
    if (status === 'active' && !s.IsActive) return false;
    if (status === 'inactive' && s.IsActive) return false;
    if (q && !(s.Name.toLowerCase().includes(q) || (s.ServiceGroup ?? '').toLowerCase().includes(q))) return false;
    if (onlyNoOwner && !hasNoOwner(s)) return false;
    if (onlyNoAssets && !hasNoAssets(s)) return false;
    if (onlyUntiered && !hasNoTier(s)) return false;
    return true;
  });

  return (
    <section>
      <header className="mb-5">
        <h1 className="font-heading text-2xl font-bold text-navy">Services</h1>
        <p className="mt-1 text-sm text-darkGrey/80">
          The shared service catalogue and what each one touches — supporting assets, in-flight CAB
          changes, and projects. Use it to assess impact before a change or decommission.
        </p>
      </header>

      <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
        <div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search services…"
            aria-label="Search services"
            className="mb-2 w-full rounded border border-border bg-white px-3 py-2 text-sm"
          />
          {/* Data-quality: surface catalogue gaps and let you filter to them. */}
          <div className="mb-2 flex flex-wrap items-center gap-1.5 text-xs text-darkGrey/70">
            <span>{services.length} {services.length === 1 ? 'service' : 'services'}</span>
            <button
              type="button"
              aria-pressed={onlyNoOwner}
              onClick={() => setOnlyNoOwner((v) => !v)}
              className={`rounded-full border px-2 py-0.5 font-semibold ${onlyNoOwner ? 'border-orange bg-orange/10 text-orange' : 'border-border text-darkGrey/70 hover:bg-bg'}`}
            >
              No owner · {noOwnerCount}
            </button>
            <button
              type="button"
              aria-pressed={onlyNoAssets}
              onClick={() => setOnlyNoAssets((v) => !v)}
              className={`rounded-full border px-2 py-0.5 font-semibold ${onlyNoAssets ? 'border-orange bg-orange/10 text-orange' : 'border-border text-darkGrey/70 hover:bg-bg'}`}
            >
              No assets · {noAssetsCount}
            </button>
            <button
              type="button"
              aria-pressed={onlyUntiered}
              onClick={() => setOnlyUntiered((v) => !v)}
              className={`rounded-full border px-2 py-0.5 font-semibold ${onlyUntiered ? 'border-orange bg-orange/10 text-orange' : 'border-border text-darkGrey/70 hover:bg-bg'}`}
            >
              Untiered · {untieredCount}
            </button>
            <label className="ml-auto flex items-center gap-1">
              <span className="font-semibold">Show</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as 'active' | 'inactive' | 'all')}
                aria-label="Filter by status"
                className="rounded border border-border bg-white px-1.5 py-0.5 text-xs text-darkGrey"
              >
                <option value="active">Active</option>
                <option value="inactive">Deactivated</option>
                <option value="all">All</option>
              </select>
            </label>
          </div>
          <div className="max-h-[70vh] divide-y divide-border overflow-y-auto rounded border border-border bg-white shadow-sm">
            {list.isLoading && <p className="px-3 py-6 text-center text-sm text-darkGrey/60">Loading…</p>}
            {list.isError && <p className="px-3 py-6 text-center text-sm text-red">Could not load services.</p>}
            {!list.isLoading && !list.isError && visible.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-darkGrey/60">No services match.</p>
            )}
            {visible.map((s) => (
              <button
                key={s.ServiceId}
                type="button"
                onClick={() => setSelectedId(s.ServiceId)}
                className={`block w-full px-3 py-2 text-left hover:bg-bg ${selectedId === s.ServiceId ? 'bg-lightBlue/10' : ''}`}
              >
                <div className="flex items-center gap-1.5 text-sm font-medium text-navy">
                  {tierMeta(s.Tier) && <span className={`rounded-full px-1.5 py-0.5 text-[0.6rem] font-bold ${tierMeta(s.Tier)!.badgeClass}`} title={tierMeta(s.Tier)!.label}>{tierMeta(s.Tier)!.short}</span>}
                  {s.Name}
                  {hasNoOwner(s) && <span className="rounded-full bg-orange/10 px-1.5 py-0.5 text-[0.6rem] font-bold uppercase text-orange" title="No owner set">no owner</span>}
                  {hasNoAssets(s) && <span className="rounded-full bg-darkGrey/10 px-1.5 py-0.5 text-[0.6rem] font-bold uppercase text-darkGrey/70" title="No assets tagged">no assets</span>}
                </div>
                <div className="flex items-center justify-between gap-2 text-xs text-darkGrey/60">
                  <span>
                    {s.ServiceGroup ?? 'Ungrouped'}
                    {!s.IsRequestable && <span className="ml-1 text-darkGrey/50">· service</span>}
                    {!s.IsActive && <span className="ml-1 text-darkGrey/50">· inactive</span>}
                  </span>
                  {s.MonthlyRecurringCost > 0 && (
                    <span className="shrink-0 font-medium text-darkGrey/70" title="Estimated monthly recurring cost from linked contracts">
                      {formatMoney(s.MonthlyRecurringCost)}/mo
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded border border-border bg-white p-5 shadow-sm">
          {selectedId == null && <p className="text-sm text-darkGrey/60">Select a service to see what it touches.</p>}
          {selectedId != null && impact.isLoading && <p className="text-sm text-darkGrey/60">Loading impact…</p>}
          {selectedId != null && impact.isError && <p className="text-sm text-red">Could not load impact for this service.</p>}
          {impact.data && <ImpactPanel data={impact.data} />}
        </div>
      </div>
    </section>
  );
}

function ImpactPanel({ data }: { data: ServiceImpact }) {
  const { Service: s, Assets, Changes, Projects, Environments, SoftwarePools } = data;
  const owner = s.PrimaryOwnerName ?? s.PrimaryOwnerEmail;
  const { canMutate } = useAuth();
  const queryClient = useQueryClient();
  const [pickerKey, setPickerKey] = useState(0);
  const [poolPickerKey, setPoolPickerKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Environment to tag a newly-linked asset with (optional).
  const [linkEnvId, setLinkEnvId] = useState<number | ''>('');

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['service-impact', s.ServiceId] });
    queryClient.invalidateQueries({ queryKey: ['services'] });
  };
  const mLink = useMutation({
    mutationFn: (assetId: number) => linkServiceAsset(s.ServiceId, assetId, linkEnvId === '' ? null : Number(linkEnvId)),
    onSuccess: () => { setError(null); setPickerKey((k) => k + 1); invalidate(); },
    onError: () => setError('Could not link that asset.'),
  });
  const mUnlink = useMutation({
    mutationFn: (assetId: number) => unlinkServiceAsset(s.ServiceId, assetId),
    onSuccess: () => { setError(null); invalidate(); },
    onError: () => setError('Could not unlink that asset.'),
  });
  const mLinkPool = useMutation({
    mutationFn: (poolId: number) => linkServicePool(s.ServiceId, poolId),
    onSuccess: () => { setError(null); setPoolPickerKey((k) => k + 1); invalidate(); },
    onError: () => setError('Could not link that licence pool.'),
  });
  const mUnlinkPool = useMutation({
    mutationFn: (poolId: number) => unlinkServicePool(s.ServiceId, poolId),
    onSuccess: () => { setError(null); invalidate(); },
    onError: () => setError('Could not unlink that licence pool.'),
  });
  const mTier = useMutation({
    mutationFn: (tier: number | null) => setServiceTier(s.ServiceId, tier),
    onSuccess: () => { setError(null); invalidate(); },
    onError: () => setError('Could not update the tier.'),
  });

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          {tierMeta(s.Tier) && <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${tierMeta(s.Tier)!.badgeClass}`} title={tierMeta(s.Tier)!.label}>{tierMeta(s.Tier)!.label}</span>}
          <h2 className="font-heading text-xl font-bold text-navy">{s.Name}</h2>
        </div>
        <p className="mt-0.5 text-sm text-darkGrey/70">
          {s.ServiceGroup ?? 'Ungrouped'}
          {owner ? <> · owner {owner}</> : null}
        </p>
        {canMutate && (
          <label className="mt-2 flex items-center gap-2 text-xs font-semibold text-darkGrey">
            Criticality tier
            <select
              value={s.Tier ?? ''}
              onChange={(e) => mTier.mutate(e.target.value === '' ? null : Number(e.target.value))}
              disabled={mTier.isPending}
              aria-label="Criticality tier"
              className="rounded border border-border bg-white px-2 py-1 text-xs font-normal text-darkGrey"
            >
              <option value="">Unclassified</option>
              {SERVICE_TIERS.map((t) => <option key={t.tier} value={t.tier}>{t.label}</option>)}
            </select>
          </label>
        )}
        {s.MonthlyRecurringCost > 0 && (
          <p className="mt-1 text-sm text-darkGrey/70">
            Est. recurring cost{' '}
            <span className="font-semibold text-navy">{formatMoney(s.MonthlyRecurringCost)}/mo</span>
            <span className="ml-1 text-xs text-darkGrey/50">· from linked contracts + licence pools (estimate, showback only)</span>
          </p>
        )}
      </div>

      <EnvironmentsSection serviceId={s.ServiceId} environments={Environments} canMutate={canMutate} onChanged={invalidate} />

      <Section icon={Laptop} title="Supporting assets" count={Assets.length}>
        {Assets.length === 0 ? (
          <Empty>No assets are tagged to this service yet.</Empty>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {Assets.map((a) => (
              <li key={a.AssetId} className="flex items-center justify-between gap-3 py-1.5">
                <span className="flex min-w-0 items-center gap-2">
                  <Link to={`/assets/${a.AssetId}`} className="truncate font-medium text-navy hover:underline">
                    {a.SerialNumber ?? a.Hostname ?? a.AssetTag ?? `Asset #${a.AssetId}`}
                  </Link>
                  {a.EnvironmentName && (
                    <span className="shrink-0 rounded-full bg-lightBlue/20 px-2 py-0.5 text-xs font-semibold text-navy">{a.EnvironmentName}</span>
                  )}
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="text-darkGrey/70">{a.Category} · {a.LifecycleState}</span>
                  {canMutate && (
                    <button
                      type="button"
                      onClick={() => mUnlink.mutate(a.AssetId)}
                      disabled={mUnlink.isPending}
                      aria-label={`Unlink ${a.SerialNumber ?? a.Hostname ?? a.AssetTag ?? `asset ${a.AssetId}`}`}
                      className="rounded p-0.5 text-darkGrey/40 hover:bg-red/10 hover:text-red disabled:opacity-50"
                    >
                      <X className="h-4 w-4" aria-hidden />
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
        {canMutate && (
          <div className="mt-3 space-y-1">
            <div className="flex gap-2">
              <div className="flex-1">
                <EntityPicker
                  key={pickerKey}
                  queryKey="service-link-asset"
                  search={assetSearch}
                  minChars={2}
                  valueLabel=""
                  onChange={(id) => { if (id) mLink.mutate(id); }}
                  placeholder="Link an asset by serial / hostname / tag…"
                  inputClassName="w-full rounded border border-border bg-white px-2 py-1.5 text-sm"
                />
              </div>
              {Environments.length > 0 && (
                <select
                  value={linkEnvId}
                  onChange={(e) => setLinkEnvId(e.target.value === '' ? '' : Number(e.target.value))}
                  aria-label="Environment for the linked asset"
                  className="shrink-0 rounded border border-border bg-white px-2 py-1.5 text-sm"
                >
                  <option value="">No environment</option>
                  {Environments.map((env) => <option key={env.EnvironmentId} value={env.EnvironmentId}>{env.Name}</option>)}
                </select>
              )}
            </div>
            {error && <p role="alert" className="text-xs text-red">{error}</p>}
          </div>
        )}
      </Section>

      <Section icon={AppWindow} title="Software" count={SoftwarePools.length}>
        {SoftwarePools.length === 0 ? (
          <Empty>No licence pools linked to this service.</Empty>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {SoftwarePools.map((p) => (
              <li key={p.SoftwarePoolId} className="flex items-center justify-between gap-3 py-1.5">
                <span className="flex min-w-0 items-center gap-2">
                  <Link to={`/software/${p.SoftwarePoolId}`} className="truncate font-medium text-navy hover:underline">{p.Title}</Link>
                  <span className="shrink-0 text-xs text-darkGrey/60">{p.LicenceType}{p.SKU ? ` · ${p.SKU}` : ''}{p.SkuId != null ? ` · #${p.SkuId}` : ''}</span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  {p.MonthlyCost > 0 && <span className="text-darkGrey/70">{formatMoney(p.MonthlyCost)}/mo</span>}
                  {canMutate && (
                    <button type="button" onClick={() => mUnlinkPool.mutate(p.SoftwarePoolId)} disabled={mUnlinkPool.isPending} aria-label={`Unlink ${p.Title}`} className="rounded p-0.5 text-darkGrey/40 hover:bg-red/10 hover:text-red disabled:opacity-50">
                      <X className="h-4 w-4" aria-hidden />
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
        {canMutate && (
          <div className="mt-3">
            <EntityPicker key={poolPickerKey} queryKey="service-link-pool" search={poolSearch} minChars={1}
              valueLabel="" onChange={(id) => { if (id) mLinkPool.mutate(id); }}
              placeholder="Link a licence pool…" inputClassName="w-full rounded border border-border bg-white px-2 py-1.5 text-sm" />
          </div>
        )}
      </Section>

      <Section icon={FileText} title="CAB changes" count={Changes.length}>
        {Changes.length === 0 ? (
          <Empty>No changes reference this service.</Empty>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {Changes.map((c) => (
              <li key={c.RFCReference} className="flex items-center justify-between gap-3 py-1.5">
                <span><span className="font-mono text-navy">{c.RFCReference}</span> <span className="text-darkGrey/80">{c.Title}</span></span>
                <span className="shrink-0 text-darkGrey/70">{c.Status} · {formatDate(c.SubmittedDate)}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section icon={FolderGit2} title="Projects" count={Projects.length}>
        {Projects.length === 0 ? (
          <Empty>No projects deliver this service.</Empty>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {Projects.map((p) => (
              <li key={p.ProjectId} className="flex items-center justify-between gap-3 py-1.5">
                <span className="text-darkGrey/90">{p.ProjectName}</span>
                <span className="text-darkGrey/70">{p.Status ?? '—'}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

const envInput = 'w-full rounded border border-border bg-white px-2 py-1.5 text-sm';

/**
 * Environments of a service (ADR-002). Helpdesk/Admin add / edit / remove the
 * environments (Production / UAT / Dev / …); assets are then tagged to one when
 * linked. Removing an environment detaches its asset links (server-side) but
 * keeps the asset↔service links.
 */
function EnvironmentsSection({ serviceId, environments, canMutate, onChanged }: {
  serviceId: number;
  environments: ServiceEnvironment[];
  canMutate: boolean;
  onChanged: () => void;
}) {
  const [mode, setMode] = useState<'idle' | 'add' | number>('idle');
  const [error, setError] = useState<string | null>(null);
  const onErr = (e: unknown) => setError(e instanceof ApiError ? e.message : 'Could not save the environment.');

  const mCreate = useMutation({
    mutationFn: (body: ServiceEnvironmentWrite) => createServiceEnvironment(serviceId, body),
    onSuccess: () => { setMode('idle'); setError(null); onChanged(); },
    onError: onErr,
  });
  const mUpdate = useMutation({
    mutationFn: ({ envId, body }: { envId: number; body: ServiceEnvironmentWrite }) => updateServiceEnvironment(serviceId, envId, body),
    onSuccess: () => { setMode('idle'); setError(null); onChanged(); },
    onError: onErr,
  });
  const mDelete = useMutation({
    mutationFn: (envId: number) => deleteServiceEnvironment(serviceId, envId),
    onSuccess: () => { setError(null); onChanged(); },
    onError: () => setError('Could not remove the environment.'),
  });
  const busy = mCreate.isPending || mUpdate.isPending;

  return (
    <Section icon={Layers} title="Environments" count={environments.length}>
      {environments.length === 0 && mode !== 'add' && (
        <Empty>No environments defined. Add UAT / Dev / DR to tag assets per environment (Production is implied).</Empty>
      )}
      {environments.length > 0 && (
        <ul className="divide-y divide-border text-sm">
          {environments.map((e) => (
            <li key={e.EnvironmentId} className="py-1.5">
              {mode === e.EnvironmentId ? (
                <EnvForm initial={e} busy={busy} onCancel={() => { setMode('idle'); setError(null); }} onSave={(body) => mUpdate.mutate({ envId: e.EnvironmentId, body })} />
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <span className="min-w-0">
                    <span className="font-medium text-navy">{e.Name}</span>
                    {e.LifecycleState && <span className="ml-2 text-xs text-darkGrey/60">{e.LifecycleState}</span>}
                    {e.Hostname && <span className="ml-2 text-xs text-darkGrey/60">{e.Hostname}</span>}
                    {e.Url && <a href={e.Url} target="_blank" rel="noreferrer" className="ml-2 text-xs text-navy underline">open</a>}
                  </span>
                  {canMutate && (
                    <span className="flex shrink-0 items-center gap-2">
                      <button type="button" onClick={() => { setError(null); setMode(e.EnvironmentId); }} className="rounded border border-border px-2 py-0.5 text-xs font-medium text-navy hover:bg-bg">Edit</button>
                      <button type="button" onClick={() => mDelete.mutate(e.EnvironmentId)} disabled={mDelete.isPending} aria-label={`Remove ${e.Name}`} className="rounded p-0.5 text-darkGrey/40 hover:bg-red/10 hover:text-red disabled:opacity-50"><X className="h-4 w-4" aria-hidden /></button>
                    </span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {canMutate && mode === 'add' && (
        <div className="mt-2"><EnvForm busy={busy} onCancel={() => { setMode('idle'); setError(null); }} onSave={(body) => mCreate.mutate(body)} /></div>
      )}
      {canMutate && mode === 'idle' && (
        <button type="button" onClick={() => { setError(null); setMode('add'); }} className="mt-2 inline-flex items-center gap-1 rounded border border-border bg-white px-2.5 py-1 text-xs font-semibold text-navy hover:bg-bg">
          <Plus className="h-3.5 w-3.5" aria-hidden /> Add environment
        </button>
      )}
      {error && <p role="alert" className="mt-1 text-xs text-red">{error}</p>}
    </Section>
  );
}

function EnvForm({ initial, onSave, onCancel, busy }: {
  initial?: ServiceEnvironment;
  onSave: (body: ServiceEnvironmentWrite) => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const [name, setName] = useState(initial?.Name ?? '');
  const [lifecycle, setLifecycle] = useState(initial?.LifecycleState ?? '');
  const [hostname, setHostname] = useState(initial?.Hostname ?? '');
  const [url, setUrl] = useState(initial?.Url ?? '');
  const save = () => {
    if (!name.trim()) return;
    onSave({ Name: name.trim(), LifecycleState: lifecycle.trim() || null, Hostname: hostname.trim() || null, Url: url.trim() || null });
  };
  return (
    <div className="space-y-2 rounded border border-border bg-bg/40 p-2">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (e.g. Production / UAT / Dev)" aria-label="Environment name" className={envInput} />
      <div className="grid grid-cols-2 gap-2">
        <input value={lifecycle} onChange={(e) => setLifecycle(e.target.value)} placeholder="Lifecycle (optional)" aria-label="Lifecycle" className={envInput} />
        <input value={hostname} onChange={(e) => setHostname(e.target.value)} placeholder="Hostname (optional)" aria-label="Hostname" className={envInput} />
      </div>
      <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="URL (optional)" aria-label="URL" className={envInput} />
      <div className="flex gap-2">
        <button type="button" onClick={save} disabled={busy || !name.trim()} className="rounded bg-orange px-3 py-1 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60">{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" onClick={onCancel} className="rounded border border-border px-3 py-1 text-xs font-medium text-darkGrey hover:bg-bg">Cancel</button>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, count, children }: { icon: typeof Laptop; title: string; count: number; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-navy">
        <Icon className="h-4 w-4 text-darkGrey/60" aria-hidden /> {title}
        <span className="rounded-full bg-bg px-2 py-0.5 text-xs font-normal text-darkGrey/70">{count}</span>
      </h3>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-darkGrey/60">{children}</p>;
}
